import axios from 'axios';
import { endpoint } from './utils.js';

export type ContractType = "" | "sent_not_signed" | "signed_pay_after_maint" | "signed_pay_before_maint";

export type ContractInfo = {
  client: number;
  contract: boolean;
  contract_signed: boolean;
  contract_interrupted: boolean;
  self_renewable: boolean;
  enable_tilt: boolean;
  contract_type: ContractType;
  validity_from: string | null;
  validity_to: string | null;
  maintenance_amount_cent: number;
  software_amount_cent: number;
  notes: string;
  __new?: true;
};

export type Invoice = {
  id: number | undefined;
  contractinfo: number;
  invoicing_date: string;
  invoice_number: number;
  validity_from: string;
  validity_to: string;
};

function contractinfoRootURL(): string {
  return endpoint("internal/current/contractinfo/");
}

function invoicesRootURL(clientId: number): string {
  return endpoint(`internal/current/contractinfo/${clientId}/invoices/`);
}

export function patchContractInfo(data: ContractInfo): ContractInfo {
  return {
    ...data,
    validity_from: data.validity_from?.split('T')[0] ?? null,
    validity_to: data.validity_to?.split('T')[0] ?? null,
  };
}

export async function fetchContractInfo(id: number): Promise<ContractInfo> {
  try {
    const res = await axios.get<ContractInfo>(`${contractinfoRootURL()}${id}/`, { withCredentials: true });
    return patchContractInfo(res.data);
  } catch (e: any) {
    if (e.response?.status === 404) {
      return {
        client: id,
        contract: false,
        contract_signed: false,
        contract_interrupted: false,
        self_renewable: false,
        enable_tilt: false,
        contract_type: "sent_not_signed",
        validity_from: null,
        validity_to: null,
        software_amount_cent: 0,
        maintenance_amount_cent: 0,
        notes: "",
        __new: true,
      };
    }
    throw e;
  }
}

export async function createContractInfo(payload: ContractInfo): Promise<ContractInfo> {
  const res = await axios.post<ContractInfo>(contractinfoRootURL(), payload, { withCredentials: true });
  return patchContractInfo(res.data);
}

export async function updateContractInfo(clientId: number, payload: ContractInfo): Promise<ContractInfo> {
  const res = await axios.put<ContractInfo>(`${contractinfoRootURL()}${clientId}/`, payload, { withCredentials: true });
  return patchContractInfo(res.data);
}

export async function fetchInvoices(clientId: number): Promise<Invoice[]> {
  try {
    const res = await axios.get<Invoice[]>(invoicesRootURL(clientId), { withCredentials: true });
    return res.data;
  } catch (e: any) {
    if (e.response?.status === 404) {
      return [];
    }
    throw e;
  }
}

export async function createInvoice(clientId: number, payload: Partial<Invoice>): Promise<Invoice> {
  const res = await axios.post<Invoice>(invoicesRootURL(clientId), payload, { withCredentials: true });
  return res.data;
}

export async function updateInvoice(clientId: number, invoiceId: number, payload: Partial<Invoice>): Promise<Invoice> {
  const res = await axios.put<Invoice>(`${invoicesRootURL(clientId)}${invoiceId}/`, payload, { withCredentials: true });
  return res.data;
}
